'use client';
import { Layout } from 'antd';
import { useAppContext } from './AppContext';
import { useTranslations } from 'next-intl';
import { logout } from '@/app/login/loginActions';
import { Icon } from './Svg';
import { useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Favorites } from '@/components/Favorites';
import { Profile } from '@/components/Profile';
import Link from 'next/link';
import { Application } from '@/interfaces/accounts';
import { FRONTEND_MAP } from '@/lib/api/config';

const { Sider, Header, Content, Footer } = Layout;

export default function MainLayout({ children }: { children: React.ReactNode }) {
  const t = useTranslations();
  const context = useAppContext();
  const [collapsed, setCollapsed] = useState(true);
  const { me } = context || {};
  const pathname = usePathname();

  const appName = useMemo(() => {
    if (pathname.startsWith('/smsinbox')) return 'SMS Inbox';
    if (pathname.startsWith('/push')) return 'Push';
    if (pathname.startsWith('/permission')) return 'Permission';
    if (pathname.startsWith('/subscription')) return 'Subscription';
    if (pathname.startsWith('/webhook')) return 'Webhook';
    if (pathname.startsWith('/linktrack')) return 'Link Track';
    if (pathname.startsWith('/smsevents')) return 'SMS Events';
    if (pathname.startsWith('/voucher')) return 'Voucher';
    return 'Portal';
  }, [pathname]);

  const customerAppList = useMemo(() => {
    const apps = me?.user?.customer?.applications ?? [];
    const list: Application[] = [];
    for (const app of apps) {
      const modifiedApp = { ...app };
      // use frontend URL if available, otherwise fall back to the original URL
      modifiedApp.mappedUrl = FRONTEND_MAP[modifiedApp.identifier] || modifiedApp.frontendUrl;
      list.push(modifiedApp);
    }
    return list;
  }, [me?.user?.customer?.applications]);

  return (
    <Layout>
      <Header className={`header${collapsed ? ' collapsed' : ''}`}>
        <div className="title container">
          <span className="bold">Umbrella</span>
          <span className="light"> | </span>
          <span className="light">{appName}</span>
        </div>

        <Profile mainportal={true} />
        {me && (
          <div className={`sidebar-btn${collapsed ? ' collapsed' : ''}`}>
            <button className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}>
              <i className="fa-solid fa-list"></i>
              {/* <i className="fa-regular fa-sidebar"></i> */}
            </button>
          </div>
        )}
        {me && (
          <div className="header-logout">
            <button onClick={logout} className="logout" title={t('LOG_OUT')}>
              <i className="fa-solid fa-sign-out-alt" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </Header>
      <Layout>
        {me && (
          <Sider
            className={`sidebar${collapsed ? ' collapsed' : ''}`}
            trigger={null}
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            collapsedWidth={0}
            width={220}
          >
            <div className="sidebar-item">
              <Link className="item" href="/">
                <div className="icon">
                  <Icon key="#umbrella_logo_menu" name="#umbrella_logo_menu" size={32} />
                </div>
                <h4>Umbrella</h4>
              </Link>
            </div>
            <div className="sidebar-item services">
              <p className="heading">{t('APPLICATIONS')}</p>
              <ul>
                {customerAppList.map((app) => {
                  const urlWithJwt = (app?.mappedUrl || app?.url) + '/#!/?jwt=' + context?.jwt;
                  return (
                    <li key={app.id}>
                      <a href={urlWithJwt}>
                        <div className="icon">
                          <Icon key={app.iconId} name={app.iconId} size={32} />
                        </div>
                        <div className="name">{app.name}</div>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
            <Favorites />
          </Sider>
        )}
        <Content className={`main-content${collapsed ? ' collapsed' : ''}`}>
          {children}
          <Footer className="footer">
            <div>
              <a href="https://linkmobility.com" className="footer-logo">
                <Icon key="#link_logo" name="#link_logo" width={180} height={50} viewBox="0 0 155.91 23.81" />
              </a>
              <span className="copyright">
                <span>
                  <Link href="/cookie">{t('COOKIE_AND_PRIVACY')}</Link>
                </span>
                <span> | </span>
                <span>
                  Copyright © <span>{new Date().getFullYear()}</span> - <span>{t('ALL_RIGHTS_RESERVED')}</span>
                </span>
              </span>
            </div>
          </Footer>
        </Content>
      </Layout>
    </Layout>
  );
}
